import React, { useState, useEffect } from 'react'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button } from '..'
import { IUIButtonProps } from '@ui/buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { get } from 'lodash'
import { Flex, HighlightedText } from '@ui'
import ClearableSelectWrapper from './ClearableSelectWrapper'
import api, { IUsageType } from '@api'
import { bookableDesks } from '@utils/space'

interface UsageTypeSelectProps {
  value?: IUsageType | null
  onChange: (usageType: IUsageType) => void
  buttonProps?: IUIButtonProps
  btnText?: string
  itemDisabled?: (usageType: IUsageType) => boolean
  multi?: boolean
  bookableOnly?: boolean
  allOptionEnable?: boolean
  filterable?: boolean
}

const UsageTypeSelect = Select2<IUsageType>
const bookableTypes = new Set(bookableDesks)

export default function UsageTypeSelectWrapper({
  value,
  onChange,
  buttonProps,
  btnText,
  itemDisabled,
  multi,
  bookableOnly,
  allOptionEnable,
  filterable = true,
}: UsageTypeSelectProps) {
  const usageTypes = useUsageTypes(bookableOnly)

  return (
    <ClearableSelectWrapper>
      <UsageTypeSelect
        resetOnSelect
        filterable={filterable}
        popoverProps={{
          minimal: true,
          position: Position.BOTTOM,
        }}
        noResults={<MenuItem disabled text="No results" />}
        items={
          allOptionEnable
            ? [{ id: 'all', name: 'all', displayName: 'All' }, ...usageTypes]
            : usageTypes
        }
        itemRenderer={(usageType, { handleClick, modifiers, query }) => {
          return modifiers.matchesPredicate ? (
            <MenuItem
              active={modifiers.active}
              key={usageType.id}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                if (multi) {
                  e.preventDefault()
                  onChange(usageType)
                  return
                }
                handleClick(e)
              }}
              text={
                <Flex align="center">
                  {multi && <Checkbox checked={itemDisabled && itemDisabled(usageType)} />}
                  <HighlightedText text={usageType.displayName} query={query} />
                </Flex>
              }
            />
          ) : null
        }}
        itemListPredicate={filterUsageTypes}
        onItemSelect={onChange}
      >
        <Button
          {...buttonProps}
          text={value ? value.displayName : btnText || 'Select'}
          rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
        />
      </UsageTypeSelect>
      <div />
    </ClearableSelectWrapper>
  )
}

const filterUsageTypes: ItemListPredicate<IUsageType> = (query, usageTypes) => {
  return query ? fuzzy.filter(usageTypes, query, { key: 'displayName' }) : usageTypes
}

let persistedState: IUsageType[] = []

export function useUsageTypes(bookableOnly?: boolean): IUsageType[] {
  const [usageTypes, setUsageTypes] = useState<IUsageType[]>(persistedState)

  useEffect(() => {
    query(bookableOnly)
  }, [bookableOnly])

  useEffect(() => {
    // Cache initial list
    if (!persistedState.length && usageTypes.length) {
      persistedState = usageTypes
    }
  }, [usageTypes])

  const query = (bookableOnly?: boolean) =>
    api.company.usageTypes().then((usageTypes) => {
      if (bookableOnly) {
        setUsageTypes(usageTypes.filter((ut) => bookableTypes.has(ut.name)))
      } else {
        setUsageTypes(usageTypes)
      }
    })

  return usageTypes
}
